/* eslint-disable complexity */
import { type Dispatch, type SetStateAction, useState } from 'react';

import {
  Box,
  Button,
  Card,
  type CardProps,
  Divider,
  Flex,
  type FlexProps,
  Grid,
  GridItem,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Spacer,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  ErrorPage,
  Icon,
  IconAi,
  IconCompletedOutline,
  IconDotsVertical,
  IconEditPencilOutline,
  IconIncompleteOutline,
  IconNotificationText,
  IconPlayVideo,
  IconPlus,
  IconSettings,
  IconShare,
  IconTrash,
  IconVideoRecorder,
  QueryWrapper,
  ShareModal,
} from '@/components';
import IconUploadCloud from '@/components/Icons/icons-generated/IconUploadCloud';
import { InterviewTitleWithEdit } from '@/components/InterviewTitleWithEdit';
import { UploadDropZone } from '@/components/UploadDropZone';
import { ADMIN_PATH } from '@/features/admin';
import { useFileAccessMutationLegacy } from '@/features/candidate/useCandidateTableMutation';
import { InterviewType, RoleType } from '@/services/api';
import { uploadObjectToS3 } from '@/utils/s3';

import { useRemoveInterviewMutation } from '../admin-interviews';
import { useGetInterviewTemplate } from '../admin-new-interview';
import { RecordIntroductionVideo } from '../admin-new-interview/IntroductionVideoRecorder';
import {
  type InterviewSettingsForm,
  InterviewSettingsModal,
} from '../admin-new-interview/NewInterviewQuestions';
import { useGetAccountQuery } from '../auth';
import { DetailBoxWithDateEditor } from './DetailBoxWithEditor';
import { EditInformationalTextModal } from './EditInformationalTextModal';
import { EditJobDescriptionModal } from './EditJobDescriptionModal';
import { usePageAdminInterviewContext } from './PageAdminInterview.context';
import { useAdminInterviewPageParams } from './PageAdminInterview.helper';
import {
  adminInterviewKeys,
  useAddInterviewInstructionVideoMutation,
  useAddTemplateToInterviewMutation,
  useGetInterviewQuery,
  useUpdateInterviewMutation,
  useUpdateInterviewSettingsMutation,
} from './useAdminInterviewQueries';
import {
  useIsAdminTemplateInterview,
  useIsTemplateInterview,
} from './useIsTemplateInterview';
import { useShareToken } from './useShareToken';

export const InterviewCard = () => {
  const { interviewId } = useAdminInterviewPageParams();
  const shareToken = useShareToken();
  const isAdminTemplateInterview = useIsAdminTemplateInterview();
  const isTemplateInterview = useIsTemplateInterview();
  const [showIntroductionVideoModal, setShowIntroductionVideoModal] =
    useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const currentUser = useGetAccountQuery();
  const { isEditable } = usePageAdminInterviewContext();

  const { t } = useTranslation(['common', 'interview']);
  const addTemplateToInterview = useAddTemplateToInterviewMutation();
  const updateInterviewMutation = useUpdateInterviewMutation();
  const queryClient = useQueryClient();
  const updateInterviewSettingsMutation = useUpdateInterviewSettingsMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getInterview({
          interviewsId: interviewId,
        }),
      });
    },
  });
  const jobDescriptionDisposure = useDisclosure();
  const informationTextDisposure = useDisclosure();
  const toast = useToast();

  const getInterviewQuery = useGetInterviewQuery(
    {
      interviewsId: interviewId,
    },
    {
      enabled: !isAdminTemplateInterview,
      axiosRequestConfig: {
        overrideSecurityToken: shareToken,
      },
    }
  );

  const getInterviewTemplateQuery = useGetInterviewTemplate(interviewId, {
    enabled: isAdminTemplateInterview,
    axiosRequestConfig: {
      overrideSecurityToken: shareToken,
    },
  });

  const createInterviewFromTemplate = async () => {
    await addTemplateToInterview.mutateAsync(interviewId);
    const url = new URL(window.location.href);
    url.searchParams.delete('isTemplate');
    window.history.replaceState({}, '', url.toString());
  };

  if (
    getInterviewQuery.error?.response?.status === 404 &&
    getInterviewTemplateQuery.error?.response?.status === 404
  ) {
    return <ErrorPage errorCode={404} />;
  }

  const handleChangeStartDate = async (date: Date) => {
    if (!interviewId || !getInterviewQuery.data?.interview) {
      return;
    }

    const { detail, endDate, title } = getInterviewQuery.data.interview;

    if (isTemplateInterview) await createInterviewFromTemplate();
    return updateInterviewMutation.mutateAsync({
      interviewsId: interviewId,
      body: {
        detail,
        endDate,
        startDate: date.toISOString(),
        title,
      },
    });
  };

  const handleChangeEndDate = async (date: Date) => {
    if (isAdminTemplateInterview) {
      await addTemplateToInterview.mutateAsync(interviewId);
      return;
    }

    if (!interviewId || !getInterviewQuery.data?.interview) {
      return;
    }

    const { detail, startDate, title } = getInterviewQuery.data.interview;

    if (isTemplateInterview) await createInterviewFromTemplate();
    return updateInterviewMutation.mutateAsync({
      interviewsId: interviewId,
      body: {
        detail,
        endDate: date.toISOString(),
        startDate,
        title,
      },
    });
  };

  const handleChangeInterviewTitleDate = async (title: string) => {
    if (!interviewId || !getInterviewQuery.data?.interview) {
      return;
    }

    if (isTemplateInterview) await createInterviewFromTemplate();
    const updateInterviewTitle = updateInterviewMutation.mutateAsync({
      interviewsId: interviewId,
      body: {
        ...getInterviewQuery.data.interview,
        title,
      },
    });

    toast.promise(updateInterviewTitle, {
      success: { title: t('interview:titleUpdate') },
      error: { title: t('interview:uploadError') },
      loading: { title: t('interview:titleUpdateLoading') },
    });

    await updateInterviewTitle;
  };

  const handleSettingsSubmit = async (data: InterviewSettingsForm) => {
    if (!interviewId) return;

    if (isTemplateInterview) await createInterviewFromTemplate();

    const settingsUpdatePromise = updateInterviewSettingsMutation.mutateAsync({
      interviewsId: interviewId,
      body: {
        interviewTitle: data.title,
        interviewType: data.type,
        isShuffleEnabled: data.isShuffleEnabled,
        isCamRecordingEnabled: data.isCamRecordingEnabled,
        isShowGuideEnabled: data.isShowGuideEnabled,
        isEmotionAnalysisEnabled: data.isEmotionAnalysisEnabled,
        isAiAvatarEnabled: data.isAiAvatarEnabled,
        isSuggestionEnabled: data.isSuggestionEnabled,
        isSecurityMeasuresEnabled: data.isSecurityMeasuresEnabled,
      },
    });

    toast.promise(settingsUpdatePromise, {
      success: { title: t('interview:interviewSettingsChanged') },
      error: { title: t('interview:interviewSettingsChangedError') },
      loading: { title: t('interview:interviewSettingsChangedLoading') },
    });

    await settingsUpdatePromise;
  };

  const borderColor = 'gray.50';

  const questionTitle =
    getInterviewQuery.data?.interview?.title ??
    getInterviewTemplateQuery.data?.template?.title;

  return (
    <>
      <Box w="full" borderRadius="xl" bgColor="white">
        <EditJobDescriptionModal
          {...jobDescriptionDisposure}
          interviewId={interviewId}
        />
        <EditInformationalTextModal
          {...informationTextDisposure}
          interviewId={interviewId}
        />
        <Flex direction="column">
          <HStack
            padding="20px 24px 16px 24px"
            w="full"
            justifyContent="space-between"
          >
            {questionTitle && (
              <InterviewTitleWithEdit
                title={questionTitle}
                onEdit={handleChangeInterviewTitleDate}
              />
            )}
            <Spacer />
            <Flex>
              <Flex h="50px" borderRadius="lg" justify="center" align="center">
                <HStack h="inherit" p={4}>
                  <Icon fontSize={20} icon={IconCompletedOutline} />
                  <Text fontWeight="light">
                    {t('common:detail.completed').toAppLocaleUpperCase()}:
                  </Text>
                  {getInterviewQuery.isLoading ? (
                    <Skeleton h="24px" w="16px" />
                  ) : (
                    <Text fontWeight="semibold">
                      {getInterviewQuery.data?.completedCandidateCount ?? 0}
                    </Text>
                  )}
                </HStack>
                <Divider
                  orientation="vertical"
                  height="30%"
                  borderColor="gray.50"
                />
                <HStack h="inherit" p={4}>
                  <Icon fontSize={20} icon={IconIncompleteOutline} />
                  <Text fontWeight="light">
                    {t('common:detail.incomplete').toAppLocaleUpperCase()}:
                  </Text>
                  {getInterviewQuery.isLoading ? (
                    <Skeleton h="24px" w="16px" />
                  ) : (
                    <Text fontWeight="semibold">
                      {getInterviewQuery.data?.candidateCount ?? 0}
                    </Text>
                  )}
                </HStack>
                {currentUser.data?.role !== RoleType.Expert && (
                  <InterviewOptions
                    onShare={() => setShowShareModal(true)}
                    onSettingsClick={() => setShowSettingsModal(true)}
                  />
                )}
              </Flex>
            </Flex>
          </HStack>
          <Divider borderColor={borderColor} />

          <QueryWrapper
            queries={[getInterviewQuery]}
            LoadingComponent={<Loading />}
          >
            <Grid
              padding="16px 24px 20px 24px"
              columnGap={3}
              rowGap={4}
              templateColumns="repeat(3, 1fr)"
            >
              <GridItem colSpan={1}>
                <DetailBox
                  title={t('common:detail.transfer').toAppLocaleUpperCase()}
                  value={
                    getInterviewQuery.data?.completedCandidateCount?.toString() ??
                    '-'
                  }
                  borderColor={borderColor}
                />
              </GridItem>
              <GridItem colSpan={1}>
                <DetailBoxWithDateEditor
                  title={t(
                    'common:timeAndDate.startDate'
                  ).toAppLocaleUpperCase()}
                  dateTime={getInterviewQuery.data?.interview?.startDate}
                  notAfter={
                    getInterviewQuery.data?.interview?.endDate
                      ? new Date(getInterviewQuery.data?.interview?.endDate)
                      : undefined
                  }
                  isLoading={
                    updateInterviewMutation.isPending ||
                    addTemplateToInterview.isPending
                  }
                  onSubmitDateTime={handleChangeStartDate}
                  borderColor={borderColor}
                  isDisabled={!!shareToken || isAdminTemplateInterview}
                />
              </GridItem>

              <GridItem colSpan={1}>
                <DetailBoxWithDateEditor
                  title={t('common:timeAndDate.endDate').toAppLocaleUpperCase()}
                  dateTime={getInterviewQuery.data?.interview?.endDate}
                  borderColor={borderColor}
                  notBefore={
                    getInterviewQuery.data?.interview?.startDate
                      ? new Date(getInterviewQuery.data?.interview?.startDate)
                      : undefined
                  }
                  isLoading={
                    updateInterviewMutation.isPending ||
                    addTemplateToInterview.isPending
                  }
                  onSubmitDateTime={handleChangeEndDate}
                  isDisabled={!!shareToken || isAdminTemplateInterview}
                />
              </GridItem>
            </Grid>
            <GridItem colSpan={3}>
              <Divider borderColor={borderColor} />
            </GridItem>

            <Grid
              padding="16px 24px 20px 24px"
              columnGap={3}
              rowGap={4}
              templateColumns="repeat(3, 1fr)"
            >
              <GridItem colSpan={1}>
                <InterviewInformationText
                  onAdd={informationTextDisposure.onOpen}
                  hasText={
                    !!getInterviewQuery.data?.interview?.informationalText
                  }
                />
              </GridItem>

              <GridItem colSpan={1}>
                <InterviewJobDescription
                  onAdd={jobDescriptionDisposure.onOpen}
                  hasDescription={
                    !!getInterviewQuery.data?.interview?.jobDescription
                  }
                />
              </GridItem>
              <GridItem colSpan={1}>
                <InterviewIntroductionVideo
                  interviewId={interviewId}
                  onAdd={() => setShowIntroductionVideoModal(true)}
                  interviewUrl={
                    getInterviewQuery.data?.interview?.instructionVideoUrl
                  }
                />
              </GridItem>
            </Grid>
          </QueryWrapper>
        </Flex>
      </Box>

      {showIntroductionVideoModal && (
        <RecordIntroductionVideo
          interviewUrl={getInterviewQuery.data?.interview?.instructionVideoUrl}
          interviewId={interviewId}
          isOpen={showIntroductionVideoModal}
          onClose={() => setShowIntroductionVideoModal(false)}
        />
      )}

      {currentUser.data?.role !== RoleType.Expert && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          interviewId={interviewId}
        />
      )}

      {currentUser.data?.role !== RoleType.Expert &&
        !getInterviewQuery.isLoading && (
          <InterviewSettingsModal
            isOpen={showSettingsModal}
            isEditable={isEditable}
            onClose={() => setShowSettingsModal(false)}
            onSubmit={handleSettingsSubmit}
            initialValues={{
              title: getInterviewQuery.data?.interview?.title ?? '',
              type:
                getInterviewQuery.data?.interview?.type ??
                InterviewType.JobInterview,
              isShowGuideEnabled:
                getInterviewQuery.data?.interview?.interviewSettings
                  ?.isShowGuideEnabled,
              isEmotionAnalysisEnabled:
                getInterviewQuery.data?.interview?.interviewSettings
                  ?.isEmotionAnalysisEnabled,
              isCamRecordingEnabled:
                getInterviewQuery.data?.interview?.interviewSettings
                  ?.isCamRecordingEnabled,
              isShuffleEnabled:
                getInterviewQuery.data?.interview?.interviewSettings
                  ?.isShuffleEnabled,
              isAiAvatarEnabled:
                getInterviewQuery.data?.interview?.interviewSettings
                  ?.isAiAvatarEnabled,
              isSuggestionEnabled:
                getInterviewQuery.data?.interview?.interviewSettings
                  ?.isSuggestionEnabled,
              isSecurityMeasuresEnabled:
                getInterviewQuery.data?.interview?.interviewSettings
                  ?.isSecurityMeasuresEnabled,
            }}
          />
        )}
    </>
  );
};

interface DetailBoxProps extends FlexProps {
  title: string;
  value?: string;
  borderColor?: string;
  onEdit?: () => void;
}

const DetailBox: React.FC<DetailBoxProps> = ({
  title,
  value,
  borderColor = 'gray.50',
  onEdit,
  ...rest
}) => {
  return (
    <Flex
      direction="column"
      py={3}
      px={4}
      justifyContent="space-between"
      h={18}
      borderWidth={1}
      borderRadius="lg"
      borderColor={borderColor}
      {...rest}
    >
      <Flex align="center" justify="space-between">
        <Text fontWeight="light" fontSize="sm">
          {title}
        </Text>

        {onEdit && (
          <Button
            p={0}
            size="20px"
            border="none"
            variant="link"
            onClick={onEdit}
          >
            <IconEditPencilOutline fontSize="20px" />
          </Button>
        )}
      </Flex>
      <Spacer />
      <Text fontSize="sm" fontWeight="semibold">
        {value}
      </Text>
    </Flex>
  );
};

interface InterviewInformationTextProps extends CardProps {
  onAdd: () => void;
  hasText?: boolean;
}

const InterviewInformationText: React.FC<InterviewInformationTextProps> = ({
  onAdd,
  hasText,
  ...rest
}) => {
  const { t } = useTranslation(['common', 'interview']);
  const shareToken = useShareToken();
  const isAdminTemplateInterview = useIsAdminTemplateInterview();

  return (
    <Card
      bg="linear-gradient(96.88deg, rgba(191, 140, 255, 0.12) 20.47%, rgba(255, 46, 0, 0.0384) 146.54%)"
      flex={1}
      display="flex"
      flexDir="column"
      py={3}
      px={4}
      justifyContent="space-between"
      {...rest}
    >
      <VStack alignItems="flex-start">
        <Stack>
          <HStack spacing={1}>
            <IconNotificationText color="brand.500" />
            <Text
              fontSize="sm"
              noOfLines={1}
              color="brand.900"
              fontWeight="medium"
            >
              {t('interview:createNewInterview.directiveText.stepTitle')}
            </Text>
          </HStack>
          <Text
            noOfLines={1}
            fontSize="xs"
            color="brand.700"
            fontWeight="normal"
          >
            {t(
              'interview:createNewInterview.directiveText.stepTitleDescription'
            )}
          </Text>
        </Stack>
        <Button
          h={8}
          onClick={onAdd}
          isDisabled={!!shareToken || isAdminTemplateInterview}
          variant="@secondary"
          leftIcon={hasText ? <IconEditPencilOutline /> : <IconPlus />}
          size="xs"
        >
          {hasText ? t('common:actions.edit') : t('common:actions.add')}
        </Button>
      </VStack>
    </Card>
  );
};

interface InterviewJobDescriptionProps extends CardProps {
  onAdd: () => void;
  hasDescription?: boolean;
}

const InterviewJobDescription: React.FC<InterviewJobDescriptionProps> = ({
  onAdd,
  hasDescription,
  ...rest
}) => {
  const { t } = useTranslation(['common', 'interview']);
  const shareToken = useShareToken();
  const isAdminTemplateInterview = useIsAdminTemplateInterview();

  return (
    <Card
      bg="linear-gradient(96.88deg, rgba(191, 140, 255, 0.12) 20.47%, rgba(255, 46, 0, 0.0384) 146.54%)"
      flex={1}
      display="flex"
      flexDir="column"
      py={3}
      px={4}
      justifyContent="space-between"
      {...rest}
    >
      <VStack alignItems="flex-start">
        <Stack>
          <HStack spacing={1}>
            <IconAi color="brand.500" />
            <Text
              fontSize="sm"
              noOfLines={1}
              color="brand.900"
              fontWeight="medium"
            >
              {t('interview:jobDescription.title')}
            </Text>
          </HStack>
          <Text
            noOfLines={1}
            fontSize="xs"
            color="brand.700"
            fontWeight="normal"
          >
            {t('interview:jobDescription.subtitle')}
          </Text>
        </Stack>
        <Button
          h={8}
          onClick={onAdd}
          isDisabled={!!shareToken || isAdminTemplateInterview}
          variant="@secondary"
          leftIcon={hasDescription ? <IconEditPencilOutline /> : <IconPlus />}
          size="xs"
        >
          {hasDescription ? t('common:actions.edit') : t('common:actions.add')}
        </Button>
      </VStack>
    </Card>
  );
};

interface InterviewIntroductionProps extends CardProps {
  onAdd: () => void;
  interviewId: string;
  interviewUrl?: string;
}

const InterviewIntroductionVideo: React.FC<InterviewIntroductionProps> = ({
  onAdd,
  interviewUrl,
  interviewId,
  ...rest
}) => {
  const { t } = useTranslation(['common', 'interview']);
  const shareToken = useShareToken();
  const { isEditable } = usePageAdminInterviewContext();

  return (
    <Card
      bg="linear-gradient(96.88deg, rgba(191, 140, 255, 0.12) 20.47%, rgba(255, 46, 0, 0.0384) 146.54%)"
      flex={1}
      display="flex"
      flexDir="column"
      py={2}
      px={4}
      justifyContent="space-between"
      {...rest}
    >
      <VStack alignItems="flex-start">
        <Stack justifyContent="center" spacing={0}>
          <HStack alignItems="center" spacing={1}>
            <IconPlayVideo fontSize={24} color="brand.500" />
            <Text
              fontSize="sm"
              noOfLines={1}
              color="brand.500"
              fontWeight="medium"
            >
              {t(
                'interview:introductionVideo.createIntroductionInterviewVideo'
              )}
            </Text>
          </HStack>
          <Text
            noOfLines={1}
            fontSize="xs"
            color="brand.900"
            fontWeight="normal"
          >
            {t('interview:introductionVideo.createIntroductionVideoNow')}
          </Text>
        </Stack>
        <HStack>
          <Button
            px={3}
            py={2}
            columnGap={2}
            h={8}
            borderRadius="8"
            onClick={onAdd}
            isDisabled={!!shareToken || !isEditable}
            fontSize={14}
            variant="@secondary"
            leftIcon={<IconVideoRecorder fontSize={18} />}
          >
            {interviewUrl
              ? t('common:actions.edit')
              : t('interview:introductionVideo.create')}
          </Button>

          <UploadIntroductionVideo
            iconOnly
            isDisabled={!isEditable}
            interviewId={interviewId}
          />
        </HStack>
      </VStack>
    </Card>
  );
};

const Loading: React.FC = () => {
  return (
    <>
      <Grid
        padding="16px 24px 20px 24px"
        columnGap={3}
        rowGap={4}
        templateColumns="repeat(3, 1fr)"
      >
        <GridItem colSpan={1}>
          <Skeleton height="48px" borderRadius="lg" />
        </GridItem>
        {/* Start Date */}
        <GridItem colSpan={1}>
          <Skeleton height="48px" borderRadius="lg" />
        </GridItem>
        {/* End Date */}
        <GridItem colSpan={1}>
          <Skeleton height="48px" borderRadius="lg" />
        </GridItem>
      </Grid>
      <GridItem colSpan={3}>
        <Divider borderColor="gray.50" />
      </GridItem>
      <Grid
        padding="16px 24px 20px 24px"
        columnGap={3}
        rowGap={4}
        templateColumns="repeat(3, 1fr)"
      >
        {/* Informational text */}
        <GridItem colSpan={1}>
          <Skeleton height="100px" borderRadius="lg" />
        </GridItem>
        {/* Job Description */}
        <GridItem colSpan={1}>
          <Skeleton height="100px" borderRadius="lg" />
        </GridItem>
        {/* Create a company promotion video */}
        <GridItem colSpan={1}>
          <Skeleton height="100px" borderRadius="lg" />
        </GridItem>
      </Grid>
    </>
  );
};

type UploadIntroductionVideoProps = {
  interviewId: string;
  setVideo?: Dispatch<SetStateAction<File | null>>;
  isDisabled?: boolean;
  iconOnly?: boolean;
};

export const UploadIntroductionVideo: React.FC<
  UploadIntroductionVideoProps
> = ({ interviewId, iconOnly = false, ...props }) => {
  const { t } = useTranslation(['common', 'interview']);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const toast = useToast();
  const isTemplateInterview = useIsTemplateInterview();

  const mediaAccessMutation = useFileAccessMutationLegacy({
    onError() {
      setIsUploadingVideo(false);
    },
  });

  const queryClient = useQueryClient();
  const interviewInstructionVideoMutation =
    useAddInterviewInstructionVideoMutation({
      async onSuccess() {
        await queryClient.invalidateQueries({
          queryKey: adminInterviewKeys.getInterview({
            interviewsId: interviewId!,
          }),
        });
      },
      onError() {
        setIsUploadingVideo(false);
      },
    });

  const submitFile = async (video: File) => {
    if (!interviewId) return;
    setIsUploadingVideo(true);
    const uploadVideoTask = handleUploadVideo(video);

    toast.promise(uploadVideoTask, {
      success: { title: t('interview:uploadSuccess') },
      error: { title: t('interview:uploadError') },
      loading: { title: t('interview:uploadingVideo') },
    });
  };

  const addTemplateToInterview = useAddTemplateToInterviewMutation();
  // eslint-disable-next-line sonarjs/no-identical-functions
  const createInterviewFromTemplate = async () => {
    await addTemplateToInterview.mutateAsync(interviewId);
    const url = new URL(window.location.href);
    url.searchParams.delete('isTemplate');
    window.history.replaceState({}, '', url.toString());
  };

  const handleUploadVideo = async (video: File) => {
    if (props.setVideo) {
      props.setVideo(video);
    }

    if (isTemplateInterview) await createInterviewFromTemplate();
    const access = await mediaAccessMutation.mutateAsync({
      candidateId: 'candidateId',
      contentType: video.type,
      interviewId: interviewId,
      questionId: (
        new Date().getTime() * 10000 +
        621355968000000000
      ).toString(),
    });

    try {
      await uploadObjectToS3({
        blob: video,
        uploadUrl: access.signedUrl!,
        headers: {
          ...access.requestHeaders,
          'content-type': video.type,
        },
      });
    } catch (error) {
      return setIsUploadingVideo(false);
    }

    await interviewInstructionVideoMutation.mutateAsync({
      id: interviewId,
      body: {
        url: access.finalObjectUrl,
      },
    });
    setIsUploadingVideo(false);
  };

  if (isUploadingVideo)
    return (
      <Button
        h={8}
        w={8}
        borderColor="gray.100"
        borderWidth="1px"
        borderStyle="solid"
        display="flex"
        bg="white"
        justifyContent="center"
        isLoading
      />
    );

  return (
    <UploadDropZone
      borderWidth="1px"
      p={0}
      minH="none"
      border="none"
      borderRadius="8"
      display="flex"
      flexDir="row"
      bg="white"
      justifyContent="center"
      cursor="pointer"
      onDrop={([file]) => file && submitFile(file)}
      dropZoneOptions={{
        accept: { 'video/mp4': ['.mp4'] },
        multiple: false,
      }}
    >
      <Button
        gap={2}
        px={3}
        py={2}
        h={8}
        borderRadius="8"
        variant="@secondary"
        fontSize={14}
        isDisabled={props.isDisabled}
      >
        {iconOnly ? null : t('common:actions.upload')}
        <IconUploadCloud fontSize={18} px={0} />
      </Button>
    </UploadDropZone>
  );
};

interface InterviewOptionsProps {
  onShare: () => void;
  onSettingsClick: () => void;
}

const InterviewOptions: React.FC<InterviewOptionsProps> = ({
  onShare,
  onSettingsClick,
}) => {
  const { interviewId } = useAdminInterviewPageParams();
  const removeInterviewMutation = useRemoveInterviewMutation();
  const toast = useToast();
  const navigate = useNavigate();

  return (
    <Menu placement="bottom-end" size="xs">
      <MenuButton
        variant="outline"
        size="xs"
        w={8}
        h={8}
        as={Button}
        justifyContent="center"
        display="flex"
        isLoading={removeInterviewMutation.isPending}
      >
        <IconDotsVertical w={2} fontSize={20} />
      </MenuButton>

      <MenuList
        overflow="hidden"
        borderColor="gray.50"
        shadow="md"
        borderRadius={12}
        p={0}
        w={36}
        minWidth={0}
      >
        <MenuItem
          transition="linear 0.1s"
          borderRadius={8}
          w={36}
          color="gray.900"
          fontWeight="medium"
          borderBottomRadius={0}
          onClick={onSettingsClick}
          fontSize="sm"
          iconSpacing={2}
          icon={<IconSettings fontSize="20" color="gray.600" />}
        >
          {t('common:generalSettings')}
        </MenuItem>
        <MenuItem
          transition="linear 0.1s"
          borderRadius={8}
          w={36}
          color="gray.900"
          fontWeight="medium"
          borderBottomRadius={0}
          onClick={onShare}
          fontSize="sm"
          iconSpacing={2}
          icon={<IconShare fontSize="20" color="gray.600" />}
        >
          {t('common:actions.share')}
        </MenuItem>
        <MenuItem
          w={36}
          fontSize="sm"
          transition="linear 0.1s"
          borderRadius={8}
          borderTopRadius={0}
          color="red.700"
          fontWeight="medium"
          isDisabled={removeInterviewMutation.isPending}
          onClick={() => {
            removeInterviewMutation.mutate(
              {
                interviewsId: interviewId,
              },
              {
                onSuccess: () => {
                  toast({
                    title: t('interview:interviewDeletedSuccess'),
                    status: 'success',
                  });
                  navigate(ADMIN_PATH);
                },
              }
            );
          }}
          iconSpacing={2}
          icon={<IconTrash fontSize={20} color="red.700" />}
        >
          {t('interview:deleteInterview')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};
